import {
  type AlertRecipient,
  type AllowAddress,
  type ControlPass,
  type Direction,
  type ExtractConfirm,
  type LinkMode,
  QuarantineReleased,
  QuarantineRow,
  RepairHint,
  type RiskAccept,
  SiteAlertsView,
  type SiteAnswers,
  SiteConsequences,
  type SiteCreate,
  SiteDetail,
  type SiteDisable,
  type SiteLaunch,
  SiteListItem,
  type SiteReissue,
  type SiteRetireLegacy,
  SiteRulePerson,
  SiteRuleSource,
  type SiteRulesPrepPut,
  SiteSecret,
  SiteSupport,
  SiteUpload,
  type UploadKind,
  UploadPreview,
} from '@tabel/shared/schemas/sites';

/** Тело запроса по схеме общих схем, у которой нет своего типа: `Body<typeof SiteLaunch>`. */
type Body<S extends { parse: (v: unknown) => unknown }> = ReturnType<S['parse']>;
import { ApiError } from '../../lib/errors';
import { contextHeaders } from '../../lib/role';

/*
 * Адреса «Настройки → Площадки» (007, T039–T041, T047, T053, T055; specs/007-sites/contracts/http-api.md). Формы ответов —
 * общие схемы `@tabel/shared/schemas/sites` (`strictObject`): ответ не по схеме — сбой сервера с кодом ошибки, а не
 * пустая страница. Все адреса — под входом сессии с заголовками роли и объекта (`contextHeaders`); права проверяет
 * сервер (`sites.*`). Тело ошибки — `{ error: { code, message, requestId, details? } }`.
 *
 * Ответ у изменяющих адресов, кроме `POST /api/sites` и `PUT …/link`, в контракте не описан: страница их тело не читает
 * и перечитывает площадку (`GET /api/sites/:site`). Секреты (`GET …/secret`, файл подключения) в кэш запросов не идут.
 */

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isText = (v: unknown): v is string => typeof v === 'string';

/** Ответ не по схеме (например, HTML вместо JSON) — сбой сервера с кодом ошибки. */
const badAnswer = (requestId: string | null) => new ApiError(500, 'INTERNAL', 'Ошибка сервера', requestId);

/** Ошибка ответа — `ApiError` с кодом, сообщением, `requestId` и `details` (номера шагов у `STEPS_NOT_READY`). */
async function failure(res: Response): Promise<ApiError> {
  const requestId = res.headers.get('x-request-id');
  const data: unknown = await res.json().catch(() => undefined);
  const e = isObject(data) && isObject(data.error) ? data.error : {};
  const code = isText(e.code) ? (e.code as ApiError['code']) : res.status === 404 ? 'NOT_FOUND' : 'INTERNAL';
  // Сессия кончилась — страница входа, как у остальных страниц (`lib/api.ts`).
  if (res.status === 401 && code === 'UNAUTHENTICATED') location.assign('/login');
  const details = isObject(e.details) ? e.details : undefined;
  return new ApiError(
    res.status,
    code,
    isText(e.message) ? e.message : `Ошибка ${res.status}`,
    isText(e.requestId) ? e.requestId : requestId,
    typeof details?.retryAfterSec === 'number' ? details.retryAfterSec : undefined,
    details,
  );
}

/**
 * Заголовки пары запроса. Список и создание — выбор переключателя (`contextHeaders`). Адреса площадки — пара
 * «администратор системы + сама площадка» (contracts/http-api.md, «Уточнения»): сервер проверяет роль на площадке из
 * адреса, а не на объекте переключателя.
 */
const siteHeaders = (code: string): Record<string, string> => ({ 'X-Role': 'sysadmin', 'X-Object': code });

async function send(
  method: string,
  url: string,
  body?: unknown | FormData,
  pair: Record<string, string> = contextHeaders(),
): Promise<Response> {
  const form = body instanceof FormData;
  let res: Response;
  try {
    // `Origin` браузер ставит сам: сервер проверяет его у изменяющих запросов.
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        ...pair,
        ...(body !== undefined && !form ? { 'Content-Type': 'application/json' } : {}),
      },
      body: form ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  if (!res.ok) throw await failure(res);
  return res;
}

/** Разбор ответа по схеме: не по схеме — ошибка, иначе страница показала бы пустые значения. */
async function parsed<T>(
  res: Response,
  schema: { safeParse(v: unknown): { success: true; data: T } | { success: false } },
): Promise<T> {
  const data: unknown = await res.json().catch(() => undefined);
  const r = schema.safeParse(data);
  if (!r.success) throw badAnswer(res.headers.get('x-request-id'));
  return r.data;
}

/** Изменяющий адрес без описанного ответа: тело не читается. */
async function done(method: string, url: string, body?: unknown | FormData): Promise<void> {
  await send(method, url, body, siteHeaders(codeOf(url)));
}

const SITES = '/api/sites';
const siteUrl = (code: string) => `${SITES}/${encodeURIComponent(code)}`;
/** Код площадки из адреса `/api/sites/<код>/…`. */
const codeOf = (url: string) => decodeURIComponent(url.slice(SITES.length + 1).split(/[/?]/)[0] ?? '');
/** Запрос к адресу площадки — с парой «администратор системы + площадка». */
const sendSite = (method: string, url: string, body?: unknown | FormData) =>
  send(method, url, body, siteHeaders(codeOf(url)));

/** Площадки, где у пользователя роль администратора системы (`SiteListItem[]`, FR-002). */
export async function sitesList(): Promise<SiteListItem[]> {
  return parsed(await send('GET', SITES), SiteListItem.array());
}

/** Шаг 1: новая площадка (FR-017). Ответ 201 — страница площадки. */
export async function createSite(body: SiteCreate): Promise<SiteDetail> {
  return parsed(await send('POST', SITES, body), SiteDetail);
}

/** Страница площадки: 10 шагов, значения для SmartPSS, терминалы, наблюдение, загрузки, тревоги, `revision`. */
export async function siteDetail(code: string): Promise<SiteDetail> {
  return parsed(await sendSite('GET', siteUrl(code)), SiteDetail);
}

/** Только названия и коды образцов с действующими правилами, по праву на подключаемую площадку. */
export async function ruleSources(code: string): Promise<SiteRuleSource[]> {
  return parsed(await sendSite('GET', `${siteUrl(code)}/rule-sources`), SiteRuleSource.array());
}

/** Кто может вести правила площадки (FR-027): действующие пользователи; администраторы правил образца отмечены. */
export async function rulePeople(code: string, source: string): Promise<SiteRulePerson[]> {
  const q = new URLSearchParams({ source });
  return parsed(await sendSite('GET', `${siteUrl(code)}/rule-people?${q}`), SiteRulePerson.array());
}

/** «Подготовить правила» (FR-004a): образец и кто ведёт правила — до запуска. */
export const prepareRules = (code: string, body: SiteRulesPrepPut) =>
  done('PUT', `${siteUrl(code)}/rules-prep`, body);

/** Ответы шагов 2, 3 («статус «Активен», а сигнала нет») и 7. */
export const putAnswers = (code: string, body: SiteAnswers) => done('PUT', `${siteUrl(code)}/answers`, body);

/** Шаг 3 и смена способа связи (FR-019, FR-061). Ответ — страница площадки. */
export async function putLink(code: string, body: { revision: number; mode: LinkMode }): Promise<SiteDetail> {
  return parsed(await sendSite('PUT', `${siteUrl(code)}/link`, body), SiteDetail);
}

/** Пароль SmartPSS — только пока шаг 4 не выполнен, потом `410 SECRET_EXPIRED`. Сервер пишет показ в журнал. */
export async function siteSecret(code: string): Promise<string> {
  return (await parsed(await sendSite('GET', `${siteUrl(code)}/secret`), SiteSecret)).password;
}

/** Новый пароль SmartPSS или новый ключ VPN: старый перестаёт работать. */
export const reissue = (code: string, body: Body<typeof SiteReissue>) =>
  done('POST', `${siteUrl(code)}/reissue`, body);

/** «Разрешить адрес» — адрес из последних отказов снимка, иначе `409 ADDRESS_NOT_SEEN`. */
export const allowAddress = (code: string, body: Body<typeof AllowAddress>) =>
  done('POST', `${siteUrl(code)}/allow-address`, body);

/** «Проверить снова» и «Повторить» в шаге 1: ответ 202. Чаще раза в 30 с — `429 WATCH_TOO_OFTEN`. */
export const recheck = (code: string) => done('POST', `${siteUrl(code)}/recheck`);

/** Направление терминала (шаг 5): новая строка с датой «сегодня» по поясу площадки. */
export const putTerminal = (code: string, ip: string, body: { revision: number; direction: Direction }) =>
  done('PUT', `${siteUrl(code)}/terminals/${encodeURIComponent(ip)}`, body);

/**
 * Шаг 6: «Прошёл вход», «Прошёл выход» с временем нажатия и «Начать заново» (`reset`). Ответ 202, итог — в странице
 * площадки.
 */
export const controlPass = (code: string, body: Body<typeof ControlPass>) =>
  done('POST', `${siteUrl(code)}/control-pass`, body);

/** Шаг 7 вручную — только после двух утр без строк выгрузки, иначе `409 EXTRACT_CONFIRM_NOT_ALLOWED`. */
export const confirmExtract = (code: string, body: Body<typeof ExtractConfirm>) =>
  done('POST', `${siteUrl(code)}/extract-confirm`, body);

/** «Продолжить с риском» — только у шага с `warn`, причина 5–500 знаков (FR-015). */
export const acceptRisk = (code: string, body: Body<typeof RiskAccept>) =>
  done('POST', `${siteUrl(code)}/risk`, body);

/**
 * Шаг 10: дата начала табелей, площадка-образец правил и кто ведёт правила площадки. Не готовы шаги — `409
 * STEPS_NOT_READY`.
 */
export const launch = (code: string, body: Body<typeof SiteLaunch>) =>
  done('POST', `${siteUrl(code)}/launch`, body);

/** Отключение (FR-062): название должно совпасть, иначе `400 CONFIRM_NAME_MISMATCH`. */
export const disable = (code: string, body: Body<typeof SiteDisable>) =>
  done('POST', `${siteUrl(code)}/disable`, body);

/** «Отменить» отключение. */
export const enable = (code: string) => done('POST', `${siteUrl(code)}/enable`);

/** «Сведения для поддержки»: текст без ФИО, адресов людей и секретов. */
export async function supportText(code: string): Promise<string> {
  return (await parsed(await sendSite('GET', `${siteUrl(code)}/support`), SiteSupport)).text;
}

/** Загрузка выгрузки SmartPSS (`multipart/form-data`, файл до 20 МБ): итог — `SiteUpload`. */
export async function upload(code: string, kind: UploadKind, file: File): Promise<SiteUpload> {
  return parsed(await sendSite('POST', uploadUrl(code, kind, false), fileForm(file)), SiteUpload);
}

/** Предпросмотр выгрузки (`dryRun=1`): строки, распознанные колонки, отклонённые с причинами, период. Без записи. */
export async function uploadPreview(code: string, kind: UploadKind, file: File): Promise<UploadPreview> {
  return parsed(await sendSite('POST', uploadUrl(code, kind, true), fileForm(file)), UploadPreview);
}

const uploadUrl = (code: string, kind: UploadKind, dryRun: boolean) =>
  `${siteUrl(code)}/uploads?kind=${encodeURIComponent(kind)}${dryRun ? '&dryRun=1' : ''}`;

function fileForm(file: File): FormData {
  const form = new FormData();
  form.append('file', file);
  return form;
}

/**
 * Подсказка периода ремонта (`GET …/repair-hint`): периоды, когда отметки могли не прийти, с причиной кодом
 * (`RepairReason`). Слова причин — в `texts.ts` (`REPAIR.reasons`).
 */
export async function repairHint(code: string): Promise<RepairHint> {
  return parsed(await sendSite('GET', `${siteUrl(code)}/repair-hint`), RepairHint);
}

/** Строки карантина (`GET …/quarantine`): `QuarantineRow[]`, новые сверху, с выпущенными. Без ФИО. */
export async function quarantine(code: string): Promise<QuarantineRow[]> {
  return parsed(await sendSite('GET', `${siteUrl(code)}/quarantine`), QuarantineRow.array());
}

/** Выпустить строки карантина в обработку: номера строк копии и причина 5–500 знаков. Ответ — сколько выпущено. */
export async function releaseQuarantine(
  code: string,
  body: { ids: number[]; reason: string },
): Promise<QuarantineReleased> {
  return parsed(await sendSite('POST', `${siteUrl(code)}/quarantine/release`, body), QuarantineReleased);
}

/** Получатель тревог площадки с автором и временем (`AlertRecipientRow`). */
export type RecipientRow = SiteAlertsView['recipients'][number];

/** Тревоги площадки и получатели (`GET …/alerts`): открытые и последние закрытые тревоги, кому о них сообщать. */
export async function siteAlerts(code: string): Promise<SiteAlertsView> {
  return parsed(await sendSite('GET', `${siteUrl(code)}/alerts`), SiteAlertsView);
}

/** Получатели тревог площадки — весь список целиком (FR-052). Ответ — тот же вид, что `GET …/alerts`. */
export async function putAlertRecipients(
  code: string,
  recipients: AlertRecipient[],
): Promise<SiteAlertsView> {
  return parsed(await sendSite('PUT', `${siteUrl(code)}/alert-recipients`, { recipients }), SiteAlertsView);
}

/**
 * Отключить прежнюю учётную запись первой площадки (`POST …/retire-legacy`, US5, research R17): с названием, как
 * отключение. Нельзя — `409 STEPS_NOT_READY` с причиной. Ответ — страница площадки.
 */
export async function retireLegacy(code: string, body: Body<typeof SiteRetireLegacy>): Promise<SiteDetail> {
  return parsed(await sendSite('POST', `${siteUrl(code)}/retire-legacy`, body), SiteDetail);
}

/** Что останется после отключения (`GET …/consequences`, FR-062, R20): отметки, люди и табели площадки. */
export async function siteConsequences(code: string): Promise<SiteConsequences> {
  return parsed(await sendSite('GET', `${siteUrl(code)}/consequences`), SiteConsequences);
}

/**
 * Файл подключения VPN `efes-<код>.conf` (WireGuard или AmneziaWG): только пока шаг 3 не выполнен, потом
 * `410 SECRET_EXPIRED`. Через `fetch` и `blob`: нужны заголовки роли и объекта. Имя — из `content-disposition`.
 */
export async function downloadVpnConfig(code: string, kind: 'wireguard' | 'amnezia'): Promise<void> {
  const res = await sendSite('GET', `${siteUrl(code)}/vpn-config?kind=${kind}`);
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) ?? /filename="?([^";]+)"?/i.exec(cd);
  const name = m?.[1] ? decodeURIComponent(m[1]) : `efes-${code}.conf`;
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** Ключи запросов «Площадок». Роль — в ключе: страницы только у администратора системы. */
export const sitesKeys = {
  all: ['sites'] as const,
  list: (role: string) => ['sites', role, 'list'] as const,
  site: (role: string, code: string) => ['sites', role, 'site', code] as const,
  ruleSources: (role: string, code: string) => ['sites', role, 'rule-sources', code] as const,
  rulePeople: (role: string, code: string, source: string) =>
    ['sites', role, 'rule-people', code, source] as const,
  /** Отдельные ключи: опрос страницы площадки (раз в 5 с) и её перечитывание их не трогают. */
  repairHint: (role: string, code: string) => ['sites', role, 'repair-hint', code] as const,
  quarantine: (role: string, code: string) => ['sites', role, 'quarantine', code] as const,
  alerts: (role: string, code: string) => ['sites', role, 'alerts', code] as const,
  consequences: (role: string, code: string) => ['sites', role, 'consequences', code] as const,
};
